// Code-action: "Extract subquery to CTE" — Slice 14.
//
// Right-click a subquery range (or place the cursor inside one) → fix surfaces
// "Extract to CTE". On apply: the subquery body is hoisted into a top-level
// CTE named `extracted_cte` (auto-numbered if taken) and the original site is
// replaced with `(SELECT * FROM extracted_cte)`. If the statement already
// carries a WITH clause, the new CTE is appended as the last comma-separated
// entry; otherwise a fresh `WITH ... ` block is prepended at statement start.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 14.
//
// Strategy:
//   1. Walk the paren stack from statement start to find the innermost `(...)`
//      enclosing the cursor (skipping strings/comments via `trySkipNonCode`).
//      The walker is the canApply trigger — if no enclosing paren exists, or
//      the cursor sits inside a string/comment, the fix doesn't apply.
//   2. Verify the inner content begins with `SELECT` (function-call args /
//      row constructors are excluded by this check; leading WITH sub-CTEs
//      are out of v1 scope to keep the extraction unambiguous).
//   3. Compose two non-overlapping byte edits: (a) inject the CTE definition
//      either at the existing WITH-tail or at the statement head; (b) replace
//      the original `(...)` with `(SELECT * FROM <name>)`.
//
// Note: the PRD references `classifyPosition === 'subquery'` as the gate, but
// classifyPosition only returns that value when the cursor sits inside an
// otherwise-empty paren — once a `SELECT` keyword is consumed, the inner
// frame's clause flips to 'select-list'. The intent of the spec ("cursor in
// a parenthesized SELECT") is captured by the paren walk + SELECT-prefix
// check below, which subsumes the classifyPosition signal.

import type { ParseOptions, SqlEngine } from '../../../sql-grammar/model';
import {
  buildParsedDocumentFromSource,
  getDocumentStructure,
} from '../../../sql-grammar/document';
import { buildLineIndex, rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import { skipContextFor, trySkipNonCode } from '../../../sql-resolver/classify/tokenSkip';
import { computeCteBindingsForRange } from '../../../sql-resolver/scope/cteScope';
import type { ParsedBuffer } from '../../../sql-resolver/types';
import { databaseEngineIdToSqlEngine } from '../../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import type {
  CodeActionContext,
  CodeActionEdit,
  CodeActionRegistryEntry,
} from '../../types';

const ENTRY_ID = 'extract-subquery-to-cte' as const;
const BASE_NAME = 'extracted_cte';

export const extractSubqueryToCteEntry: CodeActionRegistryEntry = {
  id: ENTRY_ID,
  kind: 'refactor.extract',
  title: 'Extract subquery to CTE',
  canApply: (ctx) => collectFix(ctx) !== null,
  buildEdits: (ctx) => {
    const fix = collectFix(ctx);
    return fix ? [fix] : [];
  },
};

type SubqueryRange = {
  /** Offset of the opening `(`. */
  readonly openOffset: number;
  /** Offset of the closing `)` (the byte itself, not one past). */
  readonly closeOffset: number;
};

function collectFix(ctx: CodeActionContext): CodeActionEdit | null {
  const source = ctx.sourceText;
  if (!source || source.trim().length === 0) return null;

  const sqlEngine = databaseEngineIdToSqlEngine(ctx.engineId) ?? 'mssql';
  const options: ParseOptions = {
    engine: sqlEngine,
    identifierFolding: defaultFoldingForEngine(sqlEngine),
  };
  const buffer = parseBuffer(source, options);
  if (!buffer) return null;

  // 1. Find statement scope.
  const stmt = buffer.documentStructure.statementAt(ctx.cursor.offset)
    ?? buffer.documentStructure.statements[0]
    ?? null;
  if (!stmt) return null;
  const stmtStart = stmt.range.startOffset;
  const stmtEnd = stmt.range.endOffset;

  // 2. Find the innermost `(...)` enclosing the cursor.
  const sub = findEnclosingSubquery(source, stmtStart, stmtEnd, ctx.cursor.offset, options);
  if (!sub) return null;

  // 3. Verify inner content begins with SELECT.
  const innerText = source.slice(sub.openOffset + 1, sub.closeOffset);
  if (!innerStartsWithSelect(innerText, options)) return null;

  // 4. Generate a unique CTE name.
  const existingCtes = computeCteBindingsForRange(buffer, stmtStart, stmtEnd);
  const taken = new Set<string>(existingCtes.map((c) => c.name.foldedName.toLowerCase()));
  const cteName = makeUniqueName(taken);

  // 5. Compose the two byte edits — non-overlapping, applied right-to-left.
  const lineIdx = buildLineIndex(source);
  const trimmedInner = innerText.trim();
  const replacementText = `(SELECT * FROM ${cteName})`;

  let withEdit: CodeActionEdit['textEdits'][number];
  if (existingCtes.length > 0) {
    // Append to the last CTE's body close offset.
    const last = existingCtes[existingCtes.length - 1];
    const insertOffset = last.bodyRange.endOffset;
    withEdit = {
      range: rangeFromOffsets(lineIdx, insertOffset, insertOffset),
      newText: `, ${cteName} AS (${trimmedInner})`,
    };
  } else {
    // Prepend a fresh WITH block at statement start (skipping leading
    // whitespace / line comments so the new keyword sits flush against the
    // first real token).
    const headOffset = skipLeadingNonCode(source, stmtStart, stmtEnd, options);
    withEdit = {
      range: rangeFromOffsets(lineIdx, headOffset, headOffset),
      newText: `WITH ${cteName} AS (${trimmedInner}) `,
    };
  }

  const replaceEdit = {
    range: rangeFromOffsets(lineIdx, sub.openOffset, sub.closeOffset + 1),
    newText: replacementText,
  };

  return {
    id: ENTRY_ID,
    description: `Extract subquery to CTE \`${cteName}\``,
    textEdits: [withEdit, replaceEdit],
  };
}

function findEnclosingSubquery(
  source: string,
  stmtStart: number,
  stmtEnd: number,
  cursor: number,
  options: ParseOptions,
): SubqueryRange | null {
  const ctx = skipContextFor(options);
  const stack: number[] = [];
  let cursorEnclosing: number | undefined;
  let i = stmtStart;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (i >= cursor && cursorEnclosing === undefined) {
      cursorEnclosing = stack.length > 0 ? stack[stack.length - 1] : -1;
      if (cursorEnclosing < 0) return null;
    }
    if (i >= stmtEnd) break;

    const skipped = trySkipNonCode(source, i, stmtEnd, ctx);
    if (skipped > i) {
      // If the cursor is inside a string/comment, bail.
      if (cursorEnclosing === undefined && cursor > i && cursor < skipped) return null;
      i = skipped;
      continue;
    }
    const ch = source[i];
    if (ch === '(') { stack.push(i); i++; continue; }
    if (ch === ')') {
      const open = stack.pop();
      if (cursorEnclosing !== undefined && open === cursorEnclosing) {
        return { openOffset: open, closeOffset: i };
      }
      i++;
      continue;
    }
    i++;
  }
  return null;
}

function innerStartsWithSelect(inner: string, options: ParseOptions): boolean {
  const ctx = skipContextFor(options);
  const len = inner.length;
  let i = 0;
  while (i < len) {
    const skipped = trySkipNonCode(inner, i, len, ctx);
    if (skipped > i) { i = skipped; continue; }
    const ch = inner[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { i++; continue; }
    break;
  }
  return inner.slice(i, i + 6).toUpperCase() === 'SELECT';
}

function skipLeadingNonCode(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): number {
  const ctx = skipContextFor(options);
  let i = start;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped > i) { i = skipped; continue; }
    const ch = source[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { i++; continue; }
    break;
  }
  return i;
}

function makeUniqueName(taken: ReadonlySet<string>): string {
  if (!taken.has(BASE_NAME)) return BASE_NAME;
  let n = 1;
  while (taken.has(`${BASE_NAME}_${n}`)) n++;
  return `${BASE_NAME}_${n}`;
}

function parseBuffer(source: string, options: ParseOptions): ParsedBuffer | null {
  try {
    const parsedDocument = buildParsedDocumentFromSource(source, options);
    const documentStructure = getDocumentStructure(parsedDocument, 'extract-subquery-to-cte');
    return { parsedDocument, documentStructure };
  } catch {
    return null;
  }
}

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}
